"use client";

import React from "react";
import styles from "./DoorTypeNav.module.scss";
import Link from "next/link";
import { useScrollDirection } from "@/hooks/useScrollDirection";

interface DoorTypeNavProps {
  /** Listing page path, e.g. "/cores". Filters link to `${basePath}/${slug}`. */
  basePath: string;
  label?: string | null;
  allLabel?: string | null;
  filters: Array<{ label: string; slug: string }>;
  /** Active filter slug, or undefined for "All". */
  currentFilter?: string;
}

/**
 * DoorTypeNav - Navigation to switch between a Feature Listing's filters
 * (Contentful `filter` entries). Adjusts sticky position based on scroll
 * direction (accounts for retracting header).
 */
const DoorTypeNav: React.FC<DoorTypeNavProps> = ({
  basePath,
  label,
  allLabel,
  filters,
  currentFilter,
}) => {
  const { isScrollingUp, isAtTop } = useScrollDirection();

  // When scrolling up or at top, account for header height (97px)
  // When scrolling down, header is hidden so stick to top (0px)
  const topOffset = isScrollingUp || isAtTop ? 97 : 0;

  return (
    <nav
      className={styles.doorTypeNav}
      aria-label="Filter navigation"
      style={{ top: `${topOffset}px` }}
    >
      <div className={`${styles.container} container mx-auto`}>
        <If condition={label}>
          <div className={styles.label}>{label}</div>
        </If>
        <ul className={styles.navList}>
          {[{ slug: "", label: allLabel || "All" }, ...filters].map((type) => {
            const isActive = type.slug === (currentFilter ?? "");
            return (
              <li key={type.slug || "all"}>
                <Link
                  href={type.slug ? `${basePath}/${type.slug}` : basePath}
                  className={`${styles.navLink} ${isActive ? styles.active : ""}`}
                  aria-current={isActive ? "page" : undefined}
                >
                  {type.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

// Helper component for conditional rendering
const If: React.FC<{ condition: any; children: React.ReactNode }> = ({
  condition,
  children,
}) => (condition ? <>{children}</> : null);

export default DoorTypeNav;
